import { useState, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Building2,
  Search,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  MapPin,
  QrCode,
  Download,
  Clock,
} from 'lucide-react';
import {
  useDigitalCenters,
  useCreateDigitalCenter,
  useUpdateDigitalCenter,
  useDeleteDigitalCenter,
  type DigitalCenter,
  type DigitalCenterInput,
} from '@/hooks/useDigitalCenters';
import { CERTIFICATE_TEMPLATES } from 'shared';
import { useToast } from '@/components/useToast';
import { ToastContainer } from '@/components/Toast';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;
const PT_POSTAL = /^\d{4}-\d{3}$/;
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

// ─── Validation ───────────────────────────────────────────────────────────────

type FormErrors = Partial<Record<keyof DigitalCenterInput, string>>;

function validate(form: DigitalCenterInput): FormErrors {
  const e: FormErrors = {};
  if (!form.centerName.trim()) e.centerName = 'O nome do centro é obrigatório.';
  else if (form.centerName.length > 100) e.centerName = 'Máximo de 100 caracteres.';

  if (!form.address.trim()) e.address = 'A morada é obrigatória.';
  else if (form.address.length > 150) e.address = 'Máximo de 150 caracteres.';

  if (!form.postalCode.trim()) e.postalCode = 'O código postal é obrigatório.';
  else if (!PT_POSTAL.test(form.postalCode.trim())) e.postalCode = 'Formato inválido. Use o formato 1234-123.';

  if (!form.description.trim()) e.description = 'A descrição é obrigatória.';
  else if (form.description.length > 300) e.description = 'Máximo de 300 caracteres.';

  if (!form.openingTime.trim()) e.openingTime = 'A hora de abertura é obrigatória.';
  else if (!HHMM.test(form.openingTime)) e.openingTime = 'Formato inválido (HH:MM).';

  if (!form.closingTime.trim()) e.closingTime = 'A hora de fecho é obrigatória.';
  else if (!HHMM.test(form.closingTime)) e.closingTime = 'Formato inválido (HH:MM).';

  // Cross-field: only when both are individually valid.
  if (!e.openingTime && !e.closingTime && form.openingTime >= form.closingTime) {
    e.closingTime = 'A hora de fecho tem de ser posterior à hora de abertura.';
  }

  return e;
}

const EMPTY_FORM: DigitalCenterInput = {
  centerName: '',
  address: '',
  postalCode: '',
  description: '',
  openingTime: '09:00',
  closingTime: '18:00',
  certificateTemplate: null,
};

// ─── Form field ───────────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  error?: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}

function FormField({ label, error, required, hint, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground">
        {label}{required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground/60">{hint}</p>}
      {error && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Create / Edit Modal ──────────────────────────────────────────────────────

interface CenterModalProps {
  mode: 'create' | 'edit';
  initial?: DigitalCenter;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function CenterModal({ mode, initial, onClose, onSuccess, onError }: CenterModalProps) {
  const create = useCreateDigitalCenter();
  const update = useUpdateDigitalCenter();

  const [form, setForm] = useState<DigitalCenterInput>(
    initial
      ? {
          centerName: initial.centerName,
          address: initial.address,
          postalCode: initial.postalCode,
          description: initial.description,
          openingTime: initial.openingTime,
          closingTime: initial.closingTime,
          certificateTemplate: initial.certificateTemplate ?? null,
        }
      : EMPTY_FORM,
  );
  const [errors, setErrors] = useState<FormErrors>({});
  // Server-side validation errors (422)
  const [serverErrors, setServerErrors] = useState<FormErrors>({});

  const isPending = create.isPending || update.isPending;

  const set = (key: keyof DigitalCenterInput) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setForm(prev => ({ ...prev, [key]: e.target.value }));
      // Clear error on change
      if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
      if (serverErrors[key]) setServerErrors(prev => ({ ...prev, [key]: undefined }));
    };

  const fieldError = (key: keyof DigitalCenterInput) => errors[key] || serverErrors[key];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed: DigitalCenterInput = {
      centerName: form.centerName.trim(),
      address: form.address.trim(),
      postalCode: form.postalCode.trim(),
      description: form.description.trim(),
      openingTime: form.openingTime.trim(),
      closingTime: form.closingTime.trim(),
      certificateTemplate: form.certificateTemplate || null,
    };

    const clientErrors = validate(trimmed);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    try {
      if (mode === 'create') {
        await create.mutateAsync(trimmed);
        onSuccess('Centro digital criado com sucesso.');
      } else {
        await update.mutateAsync({ id: initial!.id, data: trimmed });
        onSuccess('Centro digital actualizado com sucesso.');
      }
      onClose();
    } catch (err: unknown) {
      // Try to parse 422 validation errors from the API
      const msg = err instanceof Error ? err.message : 'Erro desconhecido.';
      // The API client throws errors where message = JSON string on 422
      try {
        const parsed = JSON.parse(msg) as { errors?: FormErrors };
        if (parsed.errors) { setServerErrors(parsed.errors); return; }
      } catch {
        // not JSON — fall through
      }
      onError(msg);
    }
  };

  const title = mode === 'create' ? 'Novo Centro Digital' : 'Editar Centro Digital';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-lg flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            <h2 id="modal-title" className="text-lg font-semibold text-foreground">
              {title}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form id="dc-form" onSubmit={handleSubmit} noValidate className="px-6 py-5 space-y-4">
          <FormField label="Nome do Centro" required error={fieldError('centerName')}>
            <input
              className={`input ${fieldError('centerName') ? 'border-destructive focus:ring-destructive/30' : ''}`}
              value={form.centerName}
              onChange={set('centerName')}
              maxLength={100}
              placeholder="ex. Centro Digital de Lisboa"
              aria-describedby={fieldError('centerName') ? 'err-centerName' : undefined}
              aria-invalid={!!fieldError('centerName')}
            />
            <div className="flex justify-end">
              <span className="text-[11px] text-muted-foreground/50 tabular-nums">
                {form.centerName.length}/100
              </span>
            </div>
          </FormField>

          <FormField label="Morada" required error={fieldError('address')}>
            <input
              className={`input ${fieldError('address') ? 'border-destructive focus:ring-destructive/30' : ''}`}
              value={form.address}
              onChange={set('address')}
              maxLength={150}
              placeholder="ex. Rua das Flores, 42"
              aria-invalid={!!fieldError('address')}
            />
            <div className="flex justify-end">
              <span className="text-[11px] text-muted-foreground/50 tabular-nums">
                {form.address.length}/150
              </span>
            </div>
          </FormField>

          <FormField
            label="Código Postal"
            required
            error={fieldError('postalCode')}
            hint="Formato: 1234-123"
          >
            <input
              className={`input w-36 ${fieldError('postalCode') ? 'border-destructive focus:ring-destructive/30' : ''}`}
              value={form.postalCode}
              onChange={set('postalCode')}
              maxLength={8}
              placeholder="1234-123"
              aria-invalid={!!fieldError('postalCode')}
            />
          </FormField>

          {/* Operating hours */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Hora de Abertura" required error={fieldError('openingTime')}>
              <input
                type="time"
                className={`input ${fieldError('openingTime') ? 'border-destructive focus:ring-destructive/30' : ''}`}
                value={form.openingTime}
                onChange={set('openingTime')}
                aria-invalid={!!fieldError('openingTime')}
              />
            </FormField>
            <FormField label="Hora de Fecho" required error={fieldError('closingTime')}>
              <input
                type="time"
                className={`input ${fieldError('closingTime') ? 'border-destructive focus:ring-destructive/30' : ''}`}
                value={form.closingTime}
                onChange={set('closingTime')}
                aria-invalid={!!fieldError('closingTime')}
              />
            </FormField>
          </div>

          <FormField label="Descrição" required error={fieldError('description')}>
            <textarea
              className={`input resize-none h-20 ${fieldError('description') ? 'border-destructive focus:ring-destructive/30' : ''}`}
              value={form.description}
              onChange={set('description')}
              maxLength={300}
              placeholder="Breve descrição do centro…"
              aria-invalid={!!fieldError('description')}
            />
            <div className="flex justify-end">
              <span className="text-[11px] text-muted-foreground/50 tabular-nums">
                {form.description.length}/300
              </span>
            </div>
          </FormField>

          <FormField
            label="Modelo de certificado"
            error={fieldError('certificateTemplate')}
            hint="Os certificados dos participantes deste centro usam este modelo."
          >
            <select
              className={`input ${fieldError('certificateTemplate') ? 'border-destructive focus:ring-destructive/30' : ''}`}
              value={form.certificateTemplate ?? ''}
              onChange={set('certificateTemplate')}
              aria-invalid={!!fieldError('certificateTemplate')}
            >
              <option value="">Sem certificado</option>
              {CERTIFICATE_TEMPLATES.map(t => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </FormField>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="dc-form"
            disabled={isPending}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === 'create' ? 'Criar Centro' : 'Guardar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Confirm Dialog ────────────────────────────────────────────────────

interface DeleteDialogProps {
  center: DigitalCenter;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function DeleteDialog({ center, onClose, onSuccess, onError }: DeleteDialogProps) {
  const del = useDeleteDigitalCenter();

  const confirm = async () => {
    try {
      await del.mutateAsync(center.id);
      onSuccess(`"${center.centerName}" eliminado com sucesso.`);
      onClose();
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : 'Erro ao eliminar.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="del-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-destructive" />
          </div>
          <div>
            <h3 id="del-title" className="text-base font-semibold text-foreground">
              Eliminar Centro Digital
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Tens a certeza que queres eliminar{' '}
              <span className="font-semibold text-foreground">"{center.centerName}"</span>?
              Esta acção não pode ser desfeita.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={del.isPending}
            className="px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={confirm}
            disabled={del.isPending}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 disabled:opacity-50 transition-colors"
          >
            {del.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── QR Code Modal ───────────────────────────────────────────────────────────

interface QRCodeModalProps {
  center: DigitalCenter;
  onClose: () => void;
}

function QRCodeModal({ center, onClose }: QRCodeModalProps) {
  const downloadQR = () => {
    if (!center.qrCode) return;
    const a = document.createElement('a');
    a.href = center.qrCode;
    a.download = `qrcode-centro-${center.id}-${center.centerName.replace(/\s+/g, '-').toLowerCase()}.png`;
    a.click();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="qr-modal-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-sm flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-primary" />
            <h2 id="qr-modal-title" className="text-base font-semibold text-foreground">
              QR Code do Centro
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 flex flex-col items-center gap-4">
          <p className="text-sm text-muted-foreground text-center">
            <span className="font-medium text-foreground">{center.centerName}</span>
          </p>

          {center.qrCode ? (
            <div className="p-3 rounded-lg border border-border bg-white">
              <img
                src={center.qrCode}
                alt={`QR Code de ${center.centerName}`}
                className="w-48 h-48 object-contain"
              />
            </div>
          ) : (
            <div className="w-48 h-48 rounded-lg border border-dashed border-border bg-muted/20 flex flex-col items-center justify-center gap-2">
              <QrCode className="w-10 h-10 text-muted-foreground/30" />
              <p className="text-xs text-muted-foreground/60 text-center px-3">
                QR Code não disponível para este registo
              </p>
            </div>
          )}

          <p className="text-xs text-muted-foreground/60 text-center">
            Conteúdo: <code className="font-mono text-foreground/70">AICD:digital-center:{center.id}</code>
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent transition-colors"
          >
            Fechar
          </button>
          {center.qrCode && (
            <button
              type="button"
              onClick={downloadQR}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Download className="w-4 h-4" />
              Descarregar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ search, onCreate }: { search: string; onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4">
      <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
        <Building2 className="w-7 h-7 text-muted-foreground/40" />
      </div>
      {search ? (
        <>
          <p className="text-base font-medium text-muted-foreground">
            Nenhum resultado para <span className="text-foreground">"{search}"</span>
          </p>
          <p className="text-sm text-muted-foreground/60">Tenta pesquisar por outro nome.</p>
        </>
      ) : (
        <>
          <p className="text-base font-medium text-muted-foreground">
            Sem centros digitais registados
          </p>
          <button
            onClick={onCreate}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Criar primeiro centro
          </button>
        </>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; center: DigitalCenter }
  | { type: 'delete'; center: DigitalCenter }
  | { type: 'qrcode'; center: DigitalCenter };

export default function CentrosDigitaisPage() {
  const { toasts, toast, dismiss } = useToast();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input
  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 350);
  }, []);

  const { data, isLoading, isError, error, isFetching } = useDigitalCenters({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    search: debouncedSearch,
  });

  const centers = data?.digitalCenters ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, total);

  const closeModal = () => setModal({ type: 'none' });

  return (
    <>
      <Helmet>
        <title>Centros Digitais | DIGGER</title>
      </Helmet>

      <div className="space-y-6">
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Building2 className="w-6 h-6 text-primary" />
              Centros Digitais
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestão dos centros digitais da rede.
            </p>
          </div>
          <button
            onClick={() => setModal({ type: 'create' })}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm shrink-0"
          >
            <Plus className="w-4 h-4" />
            Novo Centro
          </button>
        </div>

        {/* ── Toolbar ── */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-56 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="search"
              placeholder="Pesquisar por nome do centro…"
              className="input pl-9 w-full"
              value={search}
              onChange={e => handleSearch(e.target.value)}
              aria-label="Pesquisar centros digitais"
            />
          </div>
          {total > 0 && (
            <span className="text-sm text-muted-foreground tabular-nums shrink-0">
              {total} centro{total !== 1 ? 's' : ''}
              {isFetching && !isLoading && (
                <Loader2 className="inline-block w-3 h-3 ml-1.5 animate-spin text-primary" />
              )}
            </span>
          )}
        </div>

        {/* ── Table card ── */}
        <div className="card overflow-hidden">
          {/* Loading skeleton */}
          {isLoading && (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          )}

          {/* Error */}
          {isError && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <AlertTriangle className="w-8 h-8 text-destructive" />
              <p className="text-sm font-medium text-destructive">
                {error?.message ?? 'Erro ao carregar centros digitais.'}
              </p>
            </div>
          )}

          {/* Table */}
          {!isLoading && !isError && (
            <>
              {centers.length === 0 ? (
                <EmptyState
                  search={debouncedSearch}
                  onCreate={() => setModal({ type: 'create' })}
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm" aria-label="Lista de centros digitais">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">
                          Nome do Centro
                        </th>
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">
                          Morada
                        </th>
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">
                          Horário
                        </th>
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">
                          Descrição
                        </th>
                        <th scope="col" className="px-4 py-3 w-24 text-right font-medium text-muted-foreground">
                          Acções
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {centers.map(center => (
                        <tr
                          key={center.id}
                          className="hover:bg-accent/30 transition-colors"
                        >
                          {/* Center Name */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                                <Building2 className="w-4 h-4 text-primary" />
                              </div>
                              <span className="font-medium text-foreground">
                                {center.centerName}
                              </span>
                            </div>
                          </td>

                          {/* Address + Postal Code */}
                          <td className="px-4 py-3">
                            <div className="flex items-start gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-muted-foreground/60 mt-0.5 shrink-0" />
                              <span className="text-muted-foreground">
                                {center.address},{' '}
                                <span className="tabular-nums">{center.postalCode}</span>
                              </span>
                            </div>
                          </td>

                          {/* Operating hours */}
                          <td className="px-4 py-3 text-muted-foreground">
                            <div className="flex items-center gap-1.5 tabular-nums">
                              <Clock className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
                              <span>{center.openingTime} – {center.closingTime}</span>
                            </div>
                          </td>

                          {/* Description */}
                          <td className="px-4 py-3 text-muted-foreground max-w-xs">
                            <p className="truncate">{center.description}</p>
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 justify-end">
                              <button
                                onClick={() => setModal({ type: 'qrcode', center })}
                                className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                                title={`Ver QR Code de ${center.centerName}`}
                                aria-label={`Ver QR Code de ${center.centerName}`}
                              >
                                <QrCode className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setModal({ type: 'edit', center })}
                                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                                title={`Editar ${center.centerName}`}
                                aria-label={`Editar ${center.centerName}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setModal({ type: 'delete', center })}
                                className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                title={`Eliminar ${center.centerName}`}
                                aria-label={`Eliminar ${center.centerName}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── Pagination ── */}
              {total > PAGE_SIZE && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {from}–{to} de {total}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      aria-label="Página anterior"
                      className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Anterior
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let p: number;
                        if (totalPages <= 5) p = i;
                        else if (page < 3) p = i;
                        else if (page >= totalPages - 3) p = totalPages - 5 + i;
                        else p = page - 2 + i;
                        return (
                          <button
                            key={p}
                            onClick={() => setPage(p)}
                            aria-label={`Página ${p + 1}`}
                            aria-current={p === page ? 'page' : undefined}
                            className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${
                              p === page
                                ? 'bg-primary text-primary-foreground'
                                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                            }`}
                          >
                            {p + 1}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      aria-label="Próxima página"
                      className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Próximo
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {modal.type === 'create' && (
        <CenterModal
          mode="create"
          onClose={closeModal}
          onSuccess={msg => toast.success(msg)}
          onError={msg => toast.error(msg)}
        />
      )}
      {modal.type === 'edit' && (
        <CenterModal
          mode="edit"
          initial={modal.center}
          onClose={closeModal}
          onSuccess={msg => toast.success(msg)}
          onError={msg => toast.error(msg)}
        />
      )}
      {modal.type === 'delete' && (
        <DeleteDialog
          center={modal.center}
          onClose={closeModal}
          onSuccess={msg => toast.success(msg)}
          onError={msg => toast.error(msg)}
        />
      )}
      {modal.type === 'qrcode' && (
        <QRCodeModal
          center={modal.center}
          onClose={closeModal}
        />
      )}

      {/* ── Toast notifications ── */}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </>
  );
}
